import { getCountries, getCountryCallingCode } from 'libphonenumber-js/min';
import { Check, ChevronDown, Search } from 'lucide-react';
import * as React from 'react';

import { Input, InputLabel, inputContentContainerVariants } from '@/components/primitives/Input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/primitives/Popover';
import { useTranslation } from '@/hooks/useTranslation';
import { countryName, flagEmoji } from '@/lib/countries';
import { cn } from '@/lib/utils';

import type { CountryCode } from 'libphonenumber-js/min';

export interface PhoneValue {
  /** ISO-3166 alpha-2 country the picker is on. */
  country: CountryCode;
  /** The number as typed, in national form. */
  national: string;
}

export interface PhoneInputProps {
  label?: string;
  required?: boolean;
  optional?: boolean;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  /** Placeholder for the number field. */
  placeholder?: string;
  className?: string;
}

/**
 * Country picker plus national number. The pair is normalised to E.164 server-side, where
 * libphonenumber can apply each country's trunk-prefix rules.
 */
export function PhoneInput({
  label,
  required = false,
  optional = false,
  value,
  onChange,
  error,
  helperText,
  disabled = false,
  placeholder,
  className,
}: PhoneInputProps) {
  const { t, language } = useTranslation();
  const countries = React.useMemo(
    () =>
      getCountries()
        .map((code) => ({
          code,
          name: countryName(code, language),
          dial: getCountryCallingCode(code),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, language)),
    [language]
  );
  const inputId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const matches = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return countries;
    const digits = term.replace(/\D/g, '');
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.code.toLowerCase().startsWith(term) ||
        (!!digits && country.dial.startsWith(digits))
    );
  }, [countries, search]);

  const dial = getCountryCallingCode(value.country);

  return (
    <div className={cn('flex flex-col w-full', disabled && 'opacity-50', className)}>
      {label && (
        <InputLabel htmlFor={inputId} required={required} optional={optional} disabled={disabled}>
          {label}
        </InputLabel>
      )}
      <div className="flex items-start gap-xs">
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setSearch('');
          }}
        >
          <PopoverTrigger
            type="button"
            disabled={disabled}
            aria-label={`${t('Country code')}, ${countryName(value.country, language)} +${dial}`}
            className={cn(
              inputContentContainerVariants({ hasError: !!error, disabled }),
              'flex-none shrink-0 gap-xxs text-[length:var(--text-size--1)] text-foreground outline-none'
            )}
          >
            <span aria-hidden="true">{flagEmoji(value.country)}</span>
            <span>+{dial}</span>
            <ChevronDown className="size-3" aria-hidden="true" />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[260px] p-0">
            <div className="flex items-center gap-xs border-b border-solid border-border px-md py-xs">
              <Search className="size-3 shrink-0" aria-hidden="true" />
              <input
                ref={searchRef}
                aria-label={t('Search countries')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('Search')}
                className="w-full bg-transparent text-[length:var(--text-size--1)] outline-none"
              />
            </div>
            <ul
              role="listbox"
              aria-label={t('Country')}
              className="max-h-[240px] overflow-y-auto py-xxs"
              // The list is portalled out of any dialog, whose scroll lock would swallow these.
              onWheel={(event) => event.stopPropagation()}
              onTouchMove={(event) => event.stopPropagation()}
            >
              {matches.map((country) => (
                <li key={country.code} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={country.code === value.country}
                    onClick={() => {
                      onChange({ ...value, country: country.code });
                      setOpen(false);
                      setSearch('');
                    }}
                    className="flex w-full items-center gap-xs px-md py-xxs text-start text-[length:var(--text-size--1)] hover:bg-surface-hover"
                  >
                    <span aria-hidden="true">{flagEmoji(country.code)}</span>
                    <span className="flex-1 truncate">{country.name}</span>
                    <span className="text-foreground-muted">+{country.dial}</span>
                    {country.code === value.country && (
                      <Check className="size-3" aria-hidden="true" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
            {matches.length === 0 && (
              <p className="px-md py-xs text-[length:var(--text-size--1)] text-foreground-muted">
                {t('No matches')}
              </p>
            )}
          </PopoverContent>
        </Popover>
        <div className="flex-1">
          <Input
            id={inputId}
            type="tel"
            autoComplete="tel-national"
            inputMode="tel"
            aria-label={label}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            aria-required={required}
            value={value.national}
            onChange={(event) => onChange({ ...value, national: event.target.value })}
            error={error}
            helperText={helperText}
          />
        </div>
      </div>
    </div>
  );
}

PhoneInput.displayName = 'PhoneInput';
